import React, { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Lightweight Leaflet (OpenStreetMap) map. No API key required.
// Uses divIcons so we don't depend on Leaflet's bundled marker images.
export default function LeafletMap({
  center,
  zoom = 14,
  markers = [],
  userMarker,
  draggable = false,
  onDragEnd,
  onMarkerClick,
  className = "",
  style,
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const dragMarkerRef = useRef(null);

  // init map once
  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;
    const map = L.map(containerRef.current, { zoomControl: true, attributionControl: false }).setView(
      [center.lat, center.lng],
      zoom
    );
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19 }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    setTimeout(() => map.invalidateSize(), 200);
    return () => { map.remove(); mapRef.current = null; };
    // eslint-disable-next-line
  }, []);

  // recenter
  useEffect(() => {
    if (mapRef.current && center) mapRef.current.setView([center.lat, center.lng], zoom);
    // eslint-disable-next-line
  }, [center?.lat, center?.lng]);

  // draggable single marker (store position picker)
  useEffect(() => {
    if (!mapRef.current || !draggable) return;
    if (dragMarkerRef.current) mapRef.current.removeLayer(dragMarkerRef.current);
    const icon = L.divIcon({
      className: "",
      html: `<div style="transform:translate(-50%,-100%)"><div style="width:34px;height:34px;border-radius:50% 50% 50% 0;background:#E63946;transform:rotate(-45deg);box-shadow:0 4px 10px rgba(0,0,0,.3);border:3px solid #fff"></div></div>`,
      iconSize: [0, 0],
    });
    const m = L.marker([center.lat, center.lng], { draggable: true, icon }).addTo(mapRef.current);
    m.on("dragend", () => { const p = m.getLatLng(); onDragEnd && onDragEnd(p.lat, p.lng); });
    dragMarkerRef.current = m;
    // eslint-disable-next-line
  }, [draggable, center?.lat, center?.lng]);

  // offer markers
  useEffect(() => {
    if (!mapRef.current || !layerRef.current || draggable) return;
    layerRef.current.clearLayers();
    if (userMarker) {
      const uIcon = L.divIcon({
        className: "",
        html: `<div style="transform:translate(-50%,-50%)"><div style="width:16px;height:16px;border-radius:50%;background:#2563EB;border:3px solid #fff;box-shadow:0 0 0 4px rgba(37,99,235,.25)"></div></div>`,
        iconSize: [0, 0],
      });
      L.marker([userMarker.lat, userMarker.lng], { icon: uIcon }).addTo(layerRef.current);
    }
    markers.forEach((mk) => {
      if (mk.lat == null || mk.lng == null) return;
      const label = mk.label || "";
      const icon = L.divIcon({
        className: "",
        html: `<div style="transform:translate(-50%,-100%);display:flex;flex-direction:column;align-items:center">
          <div style="background:linear-gradient(135deg,#E63946,#BE123C);color:#fff;font-weight:800;font-size:12px;padding:3px 8px;border-radius:10px;box-shadow:0 3px 8px rgba(0,0,0,.25);white-space:nowrap">${label}</div>
          <div style="width:0;height:0;border-left:6px solid transparent;border-right:6px solid transparent;border-top:7px solid #BE123C"></div>
        </div>`,
        iconSize: [0, 0],
      });
      const m = L.marker([mk.lat, mk.lng], { icon }).addTo(layerRef.current);
      if (onMarkerClick) m.on("click", () => onMarkerClick(mk));
    });
    // eslint-disable-next-line
  }, [JSON.stringify(markers), userMarker?.lat, userMarker?.lng]);

  return <div ref={containerRef} className={className} style={{ width: "100%", ...style }} data-testid="leaflet-map" />;
}
